import React, { useCallback } from 'react';
import classNames from 'classnames';

import useKeyDownCallback from 'toolkit/useKeyDownCallback';
import { KeyboardEventCode } from 'toolkit/types';

import styles from './index.module.scss';

export type TabDataType = {
  id: string | number;
  isDisabled?: boolean;
  content: React.ReactNode;
};

export type TabItemProps = {
  id: string | number;
  elementId: string;
  className?: string;
  isSelected?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  children: TabDataType['content'];
  onTabChange: (id: TabDataType['id']) => void;
};

const TabItem = ({
  id,
  elementId,
  className,
  children,
  isSelected,
  isLoading,
  isDisabled,
  onTabChange,
}: TabItemProps) => {
  const onClick = useCallback(() => {
    if (!isDisabled) {
      onTabChange(id);
    }
  }, [id, onTabChange, isDisabled]);

  const onKeyDown = useKeyDownCallback<React.KeyboardEvent<HTMLDivElement>>(
    () => {
      if (!isDisabled) {
        onTabChange(id);
      }
    },
    {
      include: [KeyboardEventCode.ENTER],
    }
  );

  return (
    <div
      id={elementId}
      role="tab"
      tabIndex={0}
      className={classNames(styles.TabItem, className)}
      aria-selected={isSelected}
      data-testid={elementId}
      data-selected={isSelected}
      data-loading={isLoading}
      data-disabled={isDisabled}
      onClick={onClick}
      onKeyDown={onKeyDown}>
      {!isLoading ? <div>{children}</div> : null}
    </div>
  );
};

export default React.memo(TabItem);
